// Components
import { DirectionalHint, Icon, Modal, PrimaryButton } from "@fluentui/react";
import { ContextualMenu } from "../../../contextualMenu/ContextualMenu";
import { IconButton } from "../../../iconButton/IconButton";
import TaskEditor from "../../taskEditor/editor/TaskEditor";
import { multiPageDialogHostId } from "@ms/uno-constants/lib/local/DialogConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
// Models
import { TaskKind } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { css } from "@fluentui/utilities/lib/css";
import { getRTL } from "@fluentui/utilities/lib/rtl";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// Logging
import { useLoggers } from "../../../../hooks/useLoggers";
// Styles
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { chatRegularIconProps, copyIconProps, deleteIconProps, dismissIconProps, downIconProps, expandIconProps, leftIconProps, linkIconProps, moreIconProps, moveIconProps } from "../../../../icons/icons.styles";
import { completeTaskButtonClassName } from "../../completeTaskAnimationButton/CompleteTaskAnimationButton.styles";
import { getClassNames, getStyles } from "./TaskEditorDialog.styles";
export var TaskEditorDialogFocusOptions;
(function(TaskEditorDialogFocusOptions) {
    /** User the Default focus element */ TaskEditorDialogFocusOptions[TaskEditorDialogFocusOptions["Default"] = 0] = "Default";
    /** Override focus to the more button */ TaskEditorDialogFocusOptions[TaskEditorDialogFocusOptions["MoreOptionsButton"] = 1] = "MoreOptionsButton";
})(TaskEditorDialogFocusOptions || (TaskEditorDialogFocusOptions = {}));
/**
 * Task editor dialog which contains
 *  - A Dialog which contains the TaskEditor
 *  - Customizations of the FabricDialog chrome
 */ export function TaskEditorDialog({ contentStyles, dialogClassName, dialogContainerClassName, dialogContentClassName, editorProps, fileSuggestionsSeen, focusOverride, focusTrapZoneProps, layerProps, modalProps, onDismiss, theme }) {
    /** Ref to the more options button */ const moreOptionsButtonRef = useRef(null);
    /** Ref to the scrollable container element */ const scrollableContainerRef = useRef(null);
    const { engagementLogger } = useLoggers();
    /** Indicates if the "More" context menu is open in the task details dialog */ const [isDialogContextMenuOpen, setIsDialogContextMenuOpen] = useState(false);
    /** Indicates if the task chat panel is open */ const [isTaskChatPanelOpen, setIsTaskChatPanelOpen] = useState(editorProps.config.enableBasicPlanTaskChatMentions);
    /** Indicates if the dialog is in maximized */ const [isMaximized, setIsMaximized] = useState(false);
    /** Classnames associated with the computed styles  */ const classNames = getClassNames(getStyles(theme, editorProps.task.kind === TaskKind.Todo, isTaskChatPanelOpen, isMaximized, contentStyles));
    /** Effect for focusing on the more options button when focusOverride changes */ useEffect(()=>{
        if (focusOverride === 1) {
            setTimeout(()=>{
                moreOptionsButtonRef.current?.focus();
            }, 0);
        }
    }, [
        focusOverride
    ]);
    /**
     * Used to dismiss the dialog on click
     * @param evt The mouse event that triggered this method
     */ const handleDismiss = useCallback((evt)=>{
        const { task } = editorProps;
        if (task == null) {
            return;
        }
        if (evt) {
            evt.stopPropagation();
        }
        onDismiss(task.id);
    }, [
        editorProps,
        onDismiss
    ]);
    /**
     * Called when click on delete and return corresponding callback based on task recurrence
     * @param task
     */ const handleDelete = (task)=>{
        return task.recurrence?.isActive() ? editorProps.callbacks.openDeleteRecurringTaskDialog : editorProps.callbacks.onDeleteTask;
    };
    /**
     * Called when the "More" context menu is dismissed
     */ const onMoreContextMenuDismiss = useCallback(()=>{
        setIsDialogContextMenuOpen(false);
    }, []);
    /**
     * Called when the task details "More" button is clicked (e.g. the ellipsis)
     * @param event The event that was fired
     */ const onTaskDialogMoreButtonClicked = useCallback((event)=>{
        const { task } = editorProps;
        engagementLogger.logEngagement("TaskEditorDialog", EngagementTelemetryConstants.TaskDetailsContextMenuClicked, {
            taskCreationSourceType: task.taskCreationSourceType ?? ""
        });
        setIsDialogContextMenuOpen((prevState)=>!prevState);
    }, [
        editorProps,
        engagementLogger
    ]);
    const onChatPanelButtonClick = useCallback((e)=>{
        const { task } = editorProps;
        e.stopPropagation();
        engagementLogger.logEngagement("TaskEditorDialog", EngagementTelemetryConstants.TaskDetailsChatPanelButtonClicked, {
            taskCreationSourceType: task.taskCreationSourceType ?? "",
            isTaskChatPanelOpen: isTaskChatPanelOpen.toString()
        });
        if (!isTaskChatPanelOpen) {
            // Fetch chat messages on open
            editorProps.callbacks.onOpenChatPanel();
        }
        setIsTaskChatPanelOpen((prevState)=>!prevState);
    }, [
        editorProps,
        engagementLogger,
        isTaskChatPanelOpen
    ]);
    const onExpandButtonClick = useCallback((e)=>{
        e.stopPropagation();
        //TODO: There is no minize button in the task editor dialog design - checking with designer if we need to implement it
        setIsMaximized(true);
    }, []);
    /**
     * Callback when the floating file suggestion button is clicked
     * @param evt The mouse event
     */ const onFileSuggestionsFloatingButtonClick = useCallback((evt)=>{
        evt.stopPropagation();
        editorProps.fileSuggestionsContainerRef.current?.scrollIntoView();
    }, [
        editorProps.fileSuggestionsContainerRef
    ]);
    /**
     * Renders the floating suggestions button
     */ const renderFileSuggestionsFloatingButton = ()=>{
        if (!editorProps.config.enableFileSuggestions) {
            return null;
        }
        // File suggestions seen being null indicates the intersection observer hasn't rendered yet. In which case,
        // do not render the button to avoid flickering when rendering with suggestions for the first time
        if (fileSuggestionsSeen == null || fileSuggestionsSeen === true) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(PrimaryButton, {
            className: classNames.suggestionsFloatingButton,
            ariaLabel: Strings.TaskEditor_SuggestionsTemporalButtonText,
            onClick: onFileSuggestionsFloatingButtonClick
        }, /*#__PURE__*/ React.createElement(Icon, downIconProps), /*#__PURE__*/ React.createElement("span", {
            className: "suggestionsButtonText"
        }, Strings.TaskEditor_SuggestionsTemporalButtonText));
    };
    /**
     * Renders the context menu for the dialog
     */ const renderMoreOptionsContextMenu = ()=>{
        const { config, callbacks, task } = editorProps;
        if (!isDialogContextMenuOpen) {
            return null;
        }
        const iconPropsWithDefaultColor = (iconProps)=>({
                ...iconProps,
                className: css("defaultIconColor", iconProps.className)
            });
        const menuItems = [];
        if (config.permissions.canEdit && task.kind !== TaskKind.SharedConsumer) {
            menuItems.push({
                key: "copyTask",
                name: Strings.TaskEditorDialog_CopyTaskText,
                title: config.disableCopyTaskBySensitivity ? Strings.TaskEditorDialog_CopyTaskHelpTextWhenDisabledBySensitivity : Strings.TaskEditorDialog_CopyTaskHelpText,
                iconProps: iconPropsWithDefaultColor(copyIconProps),
                onClick: callbacks.onOpenCopyTaskDialog,
                disabled: !config.enableCopyTask
            });
            menuItems.push({
                key: "moveTask",
                name: Strings.SharedComponents_MoveTaskText,
                title: Strings.SharedComponents_MoveTaskHelpText,
                iconProps: iconPropsWithDefaultColor(moveIconProps),
                onClick: callbacks.openMoveTaskDialog,
                disabled: !config.enableMoveTask
            });
        }
        if (config.enableCopyLinkToTask && !LocalIdGenerator.isLocalTempId(task.id)) {
            menuItems.push({
                key: "copyLinkToTask",
                name: Strings.TaskEditorDialog_CopyLinkToTaskText,
                title: Strings.TaskEditorDialog_CopyLinkToTaskHelpText,
                iconProps: iconPropsWithDefaultColor(linkIconProps),
                onClick: callbacks.onCopyLinkToTask,
                disabled: task.kind === TaskKind.Todo
            });
        }
        if (config.permissions.canEdit && config.permissions.canDelete) {
            menuItems.push({
                key: "deleteTask",
                name: Strings.TaskEditorDialog_DeleteTaskText,
                title: Strings.TaskEditorDialog_DeleteTaskHelpText,
                iconProps: iconPropsWithDefaultColor(deleteIconProps),
                onClick: handleDelete(task)
            });
        }
        return /*#__PURE__*/ React.createElement(ContextualMenu, {
            shouldFocusOnMount: true,
            // For loop client, there can be multiple components, we need to target the menu to the element accordingly
            target: !!layerProps?.hostId ? `#${layerProps?.hostId} .taskEditor-dialog-topButton .moreButton` : ".taskEditor-dialog-topButton .moreButton",
            onDismiss: onMoreContextMenuDismiss,
            directionalHint: getRTL() ? DirectionalHint.bottomLeftEdge : DirectionalHint.bottomRightEdge,
            items: menuItems
        });
    };
    /**
     * Renders the right pane of the dialog, which contains the task chat panel
     */ const renderTaskChatPane = ()=>{
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.taskChatPanelContainer
        }, editorProps.callbacks.renderChatThreadControl());
    };
    /**
     * Renders the options button in the task editor dialog header
     */ const renderTopButtons = ()=>{
        const enableTaskChatMentions = editorProps.config.enableBasicPlanTaskChatMentions;
        const closeButtonAriaLabel = Strings.TaskEditorDialog_CloseButtonAriaLabel;
        let buttons = [];
        const moreButton = /*#__PURE__*/ React.createElement(IconButton, {
            key: "more",
            className: "moreButton",
            iconProps: moreIconProps,
            ariaLabel: Strings.SharedComponents_MoreButtonTooltipText,
            "aria-expanded": isDialogContextMenuOpen,
            "aria-haspopup": true,
            onClick: onTaskDialogMoreButtonClicked,
            title: Strings.SharedComponents_MoreButtonTooltipText,
            componentRef: moreOptionsButtonRef,
            id: ElementIds.TaskDetailsMoreOptionsButton
        });
        const dismissButton = /*#__PURE__*/ React.createElement(IconButton, {
            key: "dismiss",
            className: "ms-Dialog-button ms-Dialog-button--close",
            iconProps: dismissIconProps,
            ariaLabel: closeButtonAriaLabel,
            title: closeButtonAriaLabel,
            onClick: handleDismiss
        });
        if (enableTaskChatMentions) {
            const expandButton = /*#__PURE__*/ React.createElement(IconButton, {
                key: "expand",
                className: "expandButton",
                ariaLabel: Strings.TaskEditorDialog_ExpandButtonTooltipText,
                iconProps: expandIconProps,
                title: Strings.TaskEditorDialog_ExpandButtonTooltipText,
                onClick: onExpandButtonClick
            });
            const chatButton = /*#__PURE__*/ React.createElement(IconButton, {
                key: "chat",
                className: "chatButton",
                ariaLabel: Strings.TaskEditorDialog_TaskChat,
                title: Strings.TaskEditorDialog_TaskChat,
                iconProps: chatRegularIconProps,
                onClick: onChatPanelButtonClick
            });
            const backButton = /*#__PURE__*/ React.createElement(IconButton, {
                key: "back",
                className: "backButton",
                iconProps: leftIconProps,
                ariaLabel: Strings.TaskEditorDialog_BackButtonTooltipText,
                title: Strings.TaskEditorDialog_BackButtonTooltipText,
                onClick: handleDismiss
            });
            buttons = [
                chatButton,
                moreButton
            ];
            if (isMaximized) {
                return [
                    backButton,
                    ...buttons
                ];
            }
            return [
                expandButton,
                ...buttons,
                dismissButton
            ];
        }
        return [
            moreButton,
            dismissButton
        ];
    };
    /**
     * Renders the dialog portion of the component
     */ const renderDialog = ()=>{
        const id = "taskEditorDialog";
        const taskEditorChild = /*#__PURE__*/ React.createElement(React.Fragment, null, renderFileSuggestionsFloatingButton(), /*#__PURE__*/ React.createElement("div", {
            className: classNames.taskPanelContainer
        }, /*#__PURE__*/ React.createElement("div", {
            className: classNames.scrollable,
            ref: scrollableContainerRef
        }, /*#__PURE__*/ React.createElement("div", {
            className: classNames.header
        }, /*#__PURE__*/ React.createElement("p", {
            className: classNames.title,
            id: id + "-title",
            title: StringHelper.format(Strings.TaskEditorDialog_DialogAriaLabel, editorProps.task.title)
        }), /*#__PURE__*/ React.createElement("div", {
            className: classNames.topButtonContainer
        }, renderTopButtons(), renderMoreOptionsContextMenu())), /*#__PURE__*/ React.createElement("div", {
            className: css(classNames.content, dialogContentClassName)
        }, /*#__PURE__*/ React.createElement(TaskEditor, {
            ...editorProps,
            scrollContainerRef: scrollableContainerRef
        }))), isTaskChatPanelOpen && renderTaskChatPane()));
        if (isMaximized) {
            // Mount the task editor dialog to the multi-page dialog host if it is maximized
            return /*#__PURE__*/ createPortal(taskEditorChild, document.getElementById(multiPageDialogHostId) || document.body);
        }
        return /*#__PURE__*/ React.createElement(Modal, {
            allowTouchBodyScroll: true,
            className: css(classNames.root, "plannerAppDialog", dialogClassName),
            containerClassName: css(classNames.container, dialogContainerClassName),
            isOpen: true,
            layerProps: {
                ...layerProps,
                eventBubblingEnabled: true
            },
            onDismiss: handleDismiss,
            subtitleAriaId: id + "-subText",
            titleAriaId: id + "-title",
            firstFocusableSelector: completeTaskButtonClassName,
            focusTrapZoneProps: focusTrapZoneProps,
            ...modalProps
        }, taskEditorChild);
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: "taskEditorDialog"
    }, renderDialog(), editorProps.callbacks.renderDeleteRecurringTaskDialog());
}
